import React, { useCallback, useEffect, useRef, useState } from 'react';

import { DatePicker, ConfigProvider } from 'antd';
import jaJP from 'antd/es/locale/ja_JP';
import dayjs from 'dayjs';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { makePublicScheduled } from 'api/endpoints/survey';
import Button from 'components/Button';
import Input from 'components/Input';
import { ENUM_SURVEY_STATUS, ENUM_TYPE_PUBLIC_PERIOD_SETTING } from 'constants/enum';
import { toastError, toastSuccess } from 'lib/toast';
import { setOpenSchedule } from 'store/actions/survey';
import { validateBeforeNow, validateTimeSetting } from 'utils/helper';

const AthleticAbilityIcon_5 = require('assets/resources/AthleticAbilityIcon_5.svg').default;
const Cancel = require('assets/resources/Cancel.svg').default;
const ExclamationCircle2 = require('assets/resources/ExclamationCircle2.svg').default;
const IconRequired = require('assets/resources/Required.svg').default;

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:00Z';
const DISPLAY_DATE_FORMAT = 'YYYY/MM/DD HH:mm';

const getInitialTimeSetting = (type) => ({
  type,
  value: dayjs(),
});

const Schedule = () => {
  const { t } = useTranslation();
  const modalRef = useRef(null);
  const dispatch = useDispatch();
  const { isOpen, data, callBack } = useSelector((state) => state.survey.openSchedule);

  const [settingStartTime, setSettingStartTime] = useState(getInitialTimeSetting(ENUM_TYPE_PUBLIC_PERIOD_SETTING.START_TIME_NOT_SET));
  const [settingEndTime, setSettingEndTime] = useState(getInitialTimeSetting(ENUM_TYPE_PUBLIC_PERIOD_SETTING.END_TIME_NOT_SET));
  const [isOpenModalConfirm, setIsOpenModalConfirm] = useState(false);

  const closeModal = useCallback(() => {
    setSettingStartTime(getInitialTimeSetting(ENUM_TYPE_PUBLIC_PERIOD_SETTING.START_TIME_NOT_SET));
    setSettingEndTime(getInitialTimeSetting(ENUM_TYPE_PUBLIC_PERIOD_SETTING.END_TIME_NOT_SET));
    dispatch(setOpenSchedule({ isOpen: false, id: null }));
    setIsOpenModalConfirm(false);
  }, [dispatch]);

  const publicSurvey = async () => {
    try {
      const timeSetting = {
        public_start_time:
          settingStartTime?.type === ENUM_TYPE_PUBLIC_PERIOD_SETTING.START_TIME_SET ? settingStartTime?.value?.format(DATE_FORMAT) : null,
        public_end_time: settingEndTime?.type === ENUM_TYPE_PUBLIC_PERIOD_SETTING.END_TIME_SET ? settingEndTime?.value?.format(DATE_FORMAT) : null,
      };

      makePublicScheduled(data.id, timeSetting)
        .then((result) => {
          toastSuccess(result.message);
          callBack?.();
          closeModal();
        })
        .catch((error) => {
          toastError(error.message);
          closeModal();
        });
    } catch {
      return false;
    }
  };

  const onConfirm = async () => {
    const isEdit = data.status === ENUM_SURVEY_STATUS.DRAFT && (data.public_start_time || data.public_end_time);

    const error = validateTimeSetting(settingStartTime, settingEndTime);
    if (!error) {
      return;
    }

    if (isEdit) {
      setIsOpenModalConfirm(true);
    } else {
      await publicSurvey();
    }
  };

  const onChangeStartTime = (value) => {
    setSettingStartTime({ ...settingStartTime, value });
  };

  const onChangeEndTime = (value) => {
    setSettingEndTime({ ...settingEndTime, value });
  };

  const clickBackdrop = () => {
    modalRef.current.classList.add('animate-fade-out');
    setTimeout(() => {
      modalRef.current.classList.remove('animate-fade-out');
    }, 300);
  };

  // Custom header calendar picker
  const customHeaderRender = () => {
    setTimeout(() => {
      const headerView = document.querySelector('.ant-picker-header-view');
      const children = headerView?.querySelectorAll('*');

      headerView?.classList?.add('flex', 'items-center', 'justify-start');

      children.forEach((child) => {
        if (child && child?.textContent && child?.textContent?.includes('年')) {
          child.textContent = child?.textContent?.replace('年', '');
        }

        child?.classList?.add('text-black/8', '!font-normal');
      });

      if (children[1] && children[1].textContent.includes('月')) {
        headerView?.insertBefore(children[1], children[0]);
      }

      const buttonOk = document.querySelector('.ant-picker-ok > button');
      buttonOk?.classList?.add('!rounded-sm', '!h-6.5', '!w-11.5');
    }, 10);
  };

  const getBeforeChangeText = () => {
    let text = t('BEFORE_CHANGE');
    if (data.public_start_time) {
      text = text.replace('{start_time}', dayjs(data.public_start_time).format(DISPLAY_DATE_FORMAT));
    } else {
      text = text.replace('{start_time}', t('PUBLIC_NOW'));
    }
    if (data.public_end_time) {
      text = text.replace('{end_time}', dayjs(data.public_end_time).format(DISPLAY_DATE_FORMAT));
    } else {
      text = text.replace('{end_time}', t('END_TIME_NOT_SET'));
    }
    return text;
  };

  const getAfterChangeText = () => {
    let text = t('AFTER_CHANGE');
    if (settingStartTime.type === ENUM_TYPE_PUBLIC_PERIOD_SETTING.START_TIME_SET) {
      text = text.replace('{start_time}', settingStartTime?.value?.format(DISPLAY_DATE_FORMAT));
    } else {
      text = text.replace('{start_time}', t('PUBLIC_NOW'));
    }
    if (settingEndTime.type === ENUM_TYPE_PUBLIC_PERIOD_SETTING.END_TIME_SET) {
      text = text.replace('{end_time}', settingEndTime?.value?.format(DISPLAY_DATE_FORMAT));
    } else {
      text = text.replace('{end_time}', t('END_TIME_NOT_SET'));
    }
    return text;
  };

  useEffect(() => {
    if (data?.id) {
      if (data.public_start_time) {
        setSettingStartTime({ type: ENUM_TYPE_PUBLIC_PERIOD_SETTING.START_TIME_SET, value: dayjs(data.public_start_time) });
      }
      if (data.public_end_time) {
        setSettingEndTime({ type: ENUM_TYPE_PUBLIC_PERIOD_SETTING.END_TIME_SET, value: dayjs(data.public_end_time) });
      }
    }
  }, [data]);

  useEffect(() => {
    document.body.classList.add('overflow-hidden');
    document.body.classList.add('h-screen');
    return () => {
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('h-screen');
    };
  }, []);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div id="modal" className="fixed inset-0 flex-middle-center bg-gray-800 bg-opacity-50 z-50">
      <div onClick={clickBackdrop} className="fixed inset-0 backdrop" />
      {isOpenModalConfirm ? (
        <div ref={modalRef} className={'bg-white rounded-sm shadow-lg p-8 pb-6 relative max-w-104 w-full modal-content'}>
          <div data-testid="header-modal" id="header-modal" className="flex items-start">
            <div className="text-orange-500 mr-2 text-2xl min-w-6 min-h-6">
              <img src={ExclamationCircle2} alt="exclamation" width={22} height={22} />
            </div>
            <div>
              <h2 className="text-base font-bold text-logo">{t('CHANGE_SETTING_TITLE')}</h2>
              <h2 className="text-base font-bold text-logo">{t('CHANGE_SETTING_DESCRIPTION')}</h2>
            </div>
          </div>

          <div className="mt-6 pl-7.5">
            <div className=" text-sm text-secondary-354" dangerouslySetInnerHTML={{ __html: getBeforeChangeText() }}></div>
            <div className="flex-middle-center my-2 rotate-180">
              <img src={AthleticAbilityIcon_5} alt="icon" width={16} height={16} />
            </div>
            <div className="text-sm text-black-102" dangerouslySetInnerHTML={{ __html: getAfterChangeText() }}></div>
          </div>

          <div data-testid="footer-modal" id="footer-modal" className="mt-6 flex justify-end gap-2">
            <Button
              onClick={() => setIsOpenModalConfirm(false)}
              text={t('CANCEL_TEXT')}
              color="outline"
              className="w-25.5 px-3.75 h-8 text-sm text-black/85"
            />
            <Button
              onClick={publicSurvey}
              text={t('CHANGE_SETTING')}
              color="primary"
              className={'w-22 h-8 px-3.75 text-sm border border-solid border-primary-423'}
            />
          </div>
        </div>
      ) : (
        <div ref={modalRef} className="bg-white rounded-sm shadow-lg relative max-w-120 w-120 modal-content">
          <div className="flex-between-center px-6 py-4 ">
            <p className="font-bold">{t('PUBLIC_PERIOD_SETTING')}</p>
          </div>
          <div className="p-3 relative">
            <div className="absolute top-0 left-0 w-full border-t border-white-720 h-0.25"></div>
            <div className="max-h-138 w-full overflow-y-auto p-3 grid grid-cols-1">
              <div className="flex flex-col">
                {!validateTimeSetting(settingStartTime, settingEndTime) && (
                  <div className="flex flex-col gap-1 mb-4">
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <img src={Cancel} alt="cancel" width={12} height={12} />
                      {t('START_TIME_BEFORE_END_TIME')}
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <img className="cursor-pointer" src={IconRequired} alt="icon required" width={7} height={22} />
                  <p className="text-black/85">{t('START_TIME')}</p>
                </div>
                <div className="flex items-center gap-4 mt-4">
                  <Input
                    className="!w-auto"
                    onChange={() => setSettingStartTime({ ...settingStartTime, type: ENUM_TYPE_PUBLIC_PERIOD_SETTING.START_TIME_NOT_SET })}
                    checked={settingStartTime?.type === ENUM_TYPE_PUBLIC_PERIOD_SETTING.START_TIME_NOT_SET}
                    type="radio-cus"
                    text={t('PUBLIC_NOW')}
                    name="start-time-setting"
                    id="start-time-not-set"
                  />
                  <Input
                    className="!w-auto"
                    onChange={() => setSettingStartTime({ ...settingStartTime, type: ENUM_TYPE_PUBLIC_PERIOD_SETTING.START_TIME_SET })}
                    checked={settingStartTime?.type === ENUM_TYPE_PUBLIC_PERIOD_SETTING.START_TIME_SET}
                    type="radio-cus"
                    text={t('PUBLIC_SPECIFY_DATE')}
                    name="start-time-setting"
                    id="start-time-set"
                  />
                </div>
                {settingStartTime?.type === ENUM_TYPE_PUBLIC_PERIOD_SETTING.START_TIME_SET && (
                  <div className="flex items-center mt-2">
                    <ConfigProvider locale={jaJP}>
                      <DatePicker
                        onOpenChange={(open) => open && customHeaderRender()}
                        onPanelChange={customHeaderRender}
                        onPickerValueChange={customHeaderRender}
                        placeholder={t('PLACEHOLDER_START_TIME')}
                        format={DISPLAY_DATE_FORMAT}
                        className={`w-64 h-10 rounded-sm ${!validateTimeSetting(settingStartTime, settingEndTime) ? 'border-danger-229' : 'border-gray-651'}`}
                        defaultValue={settingStartTime?.value}
                        showTime
                        onChange={onChangeStartTime}
                        showNow={false}
                      />
                    </ConfigProvider>
                  </div>
                )}
              </div>

              <div className="flex flex-col mt-6">
                <div className="flex items-center gap-1">
                  <img className="cursor-pointer" src={IconRequired} alt="icon required" width={7} height={22} />
                  <p className="text-black/85">{t('END_TIME')}</p>
                </div>
                <div className="flex items-center gap-4 mt-4">
                  <Input
                    className="!w-auto"
                    onChange={() => setSettingEndTime({ ...settingEndTime, type: ENUM_TYPE_PUBLIC_PERIOD_SETTING.END_TIME_NOT_SET })}
                    checked={settingEndTime?.type === ENUM_TYPE_PUBLIC_PERIOD_SETTING.END_TIME_NOT_SET}
                    type="radio-cus"
                    text={t('END_TIME_NOT_SET')}
                    name="end-time-setting"
                    id="end-time-not-set"
                  />
                  <Input
                    className="!w-auto"
                    onChange={() => setSettingEndTime({ ...settingEndTime, type: ENUM_TYPE_PUBLIC_PERIOD_SETTING.END_TIME_SET })}
                    checked={settingEndTime?.type === ENUM_TYPE_PUBLIC_PERIOD_SETTING.END_TIME_SET}
                    type="radio-cus"
                    text={t('PUBLIC_SPECIFY_DATE')}
                    name="end-time-setting"
                    id="end-time-set"
                  />
                </div>

                {settingEndTime?.type === ENUM_TYPE_PUBLIC_PERIOD_SETTING.END_TIME_SET && (
                  <div className="flex items-center mt-2">
                    <ConfigProvider locale={jaJP}>
                      <DatePicker
                        onOpenChange={(open) => open && customHeaderRender()}
                        onPanelChange={customHeaderRender}
                        onPickerValueChange={customHeaderRender}
                        placeholder={t('PLACEHOLDER_END_TIME')}
                        format={DISPLAY_DATE_FORMAT}
                        className={`w-64 h-10 rounded-sm ${!validateTimeSetting(settingStartTime, settingEndTime) ? 'border-danger-229' : 'border-gray-651'}`}
                        defaultValue={settingEndTime?.value}
                        showTime
                        onChange={onChangeEndTime}
                        showNow={false}
                      />
                    </ConfigProvider>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="h-16 border-t border-white-720 flex-end-center gap-2 py-4 px-6">
            <Button className="h-8 text-sm w-25.5" text={t('CANCEL_TEXT')} color="outline" onClick={closeModal} />
            {settingStartTime && settingEndTime && validateBeforeNow(settingStartTime, settingEndTime) ? (
              <Button className={'h-8 text-sm w-34'} text={t('PUBLIC_CONFIRM')} color={'primary'} onClick={onConfirm} />
            ) : (
              <Button
                className="h-8 text-sm w-34 border-gray-651 bg-white-795 !text-black/25 cursor-not-allowed"
                color="outline"
                text={t('PUBLIC_CONFIRM')}
              />
            )}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default React.memo(Schedule);
